import { useState } from 'react';

import { ChevronDown } from 'lucide-react';
import { DateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import i18n from '@/core/i18n';
import { createDateRangeToStringFormatter, stringToDateRange } from '@/helpers';
import { formatDate as formatDateLib } from '@/lib/format';
import { cn } from '@/lib/utils';
import { DateRangeCalendar } from '@/modules/common/components/molecules/date/date-range-calendar';
import { DateRangeFilter } from '@/types/filters';

interface Props {
  config: DateRangeFilter;
  value: string;
  onChange: (key: string, value: string) => void;
}

export const FilterDateRange = ({ config, value, onChange }: Props) => {
  const [open, setOpen] = useState(false);

  const dateRange = stringToDateRange(value);

  const formatDate = date =>
    formatDateLib(date, { month: 'short', day: 'numeric', year: 'numeric' });

  const dateRangeToString = createDateRangeToStringFormatter(formatDate);

  const getLabelText = () => {
    const normalizedValue = dateRange ? dateRangeToString(dateRange) : '';
    const selectedOption = config.options?.find(
      option => dateRangeToString(option.value) === normalizedValue
    );

    if (selectedOption) return ': ' + selectedOption.label;

    if (dateRange?.from || dateRange?.to)
      return `: ${formatDate(dateRange?.from) || '...'} - ${formatDate(dateRange?.to) || '...'}`;

    return '';
  };

  const handleChange = (value: DateRange | null) =>
    onChange(config.name, value ? dateRangeToString(value) : '');

  const handleClear = () => {
    handleChange(null);
    setOpen(false);
  };

  const label = getLabelText();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn('h-9 px-2 py-1 border rounded text-sm text-left', open && 'bg-muted')}
      >
        <div className="flex items-center justify-between gap-2">
          <span>
            {config.label}
            <span className="font-medium">{label}</span>
          </span>
          <ChevronDown className={cn('transition', open && 'rotate-180')} size={16} />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto space-y-3 p-0" side="bottom" align="start">
        <DateRangeCalendar
          value={dateRange}
          options={config?.options}
          onChange={handleChange}
          footer={
            <div className="flex justify-between p-2">
              <Button size="sm" type="button" onClick={handleClear} variant="ghost">
                {i18n.t('common:filters.clearAll')}
              </Button>
            </div>
          }
        />
      </PopoverContent>
    </Popover>
  );
};
